/**
 * useIssueLayerDetection - Auto-registers the Issues layer when a document has review results
 *
 * Task 239.1: Bridges the gap between "review workflow completed" and
 * "Issues layer appears in the left panel".
 *
 * The issues API requires a WORKFLOW PROJECT ID (not a matter ID).
 * This hook uses useMatterWorkflowsQuery for shared/cached workflow fetching,
 * then probes each review workflow for issues matching the active document.
 */

import { useEffect, useState } from 'react';
import { useLayerStore, layerSelectors } from '../stores/layerStore';
import { useMatterWorkflowsQuery } from '../queries/useMatterQueries';
import { issueApiService } from '../services/issueApi';

// Module-level cache shared across all hook instances to prevent duplicate probes
// when multiple components (DocumentViewer, SplitDocumentView) mount simultaneously.
const probedDocuments = new Set<string>();
const inFlightProbes = new Map<string, Promise<void>>();

// Issue #771 follow-up: subscriber bumps. Clearing the probe cache alone
// doesn't trigger the layer-detection effect to re-run — the effect's deps
// include workflowsData, which TanStack reuses by reference (structural
// sharing) when the workflows list itself hasn't changed (only its
// underlying issues did). So a dismiss-all or section-run that doesn't add
// a NEW workflow row leaves the effect dormant and the layer state stale.
//
// Each mounted hook registers a tiny state-bumper; clearIssueProbeCache
// invokes them all to force a re-render + effect re-run.
const probeBumpers = new Set<() => void>();

/** Clear probe cache for a document (e.g., after new analysis completes). */
export function clearIssueProbeCache(documentId?: string) {
  if (documentId) {
    probedDocuments.delete(documentId);
    inFlightProbes.delete(documentId);
  } else {
    probedDocuments.clear();
    inFlightProbes.clear();
  }
  probeBumpers.forEach((bump) => bump());
}

/**
 * Detect whether a document has review issues and register the Issues layer.
 *
 * @param matterId - The matter ID (NOT the project/workflow ID)
 * @param documentId - The active document ID
 */
export function useIssueLayerDetection(
  matterId: string | null,
  documentId: string | null
): void {
  const addAvailableLayer = useLayerStore((state) => state.addAvailableLayer);
  const removeAvailableLayer = useLayerStore((state) => state.removeAvailableLayer);
  const clearLayerSource = useLayerStore((state) => state.clearLayerSource);

  const { data: workflowsData } = useMatterWorkflowsQuery(matterId);

  // Issue #771 follow-up: re-render counter bumped by clearIssueProbeCache.
  // Used as an effect dep so the probe re-runs even when workflowsData
  // hasn't changed by reference (the common case for dismiss-all + re-run).
  const [probeBump, setProbeBump] = useState(0);
  useEffect(() => {
    const bump = () => setProbeBump((n) => n + 1);
    probeBumpers.add(bump);
    return () => {
      probeBumpers.delete(bump);
    };
  }, []);

  useEffect(() => {
    if (!matterId || !documentId || !workflowsData) return;

    // Issue #771 follow-up: stale-source check. The layerStore is persisted
    // to localStorage; a sourceAnalysisRunId pointing to a workflow that no
    // longer exists (deleted, or hydrated from a different machine's state)
    // would make useDocumentIssues fetch 500-returning IDs forever, because
    // the anti-stomp guard in addAvailableLayer refuses to overwrite a
    // source ID once an explicit marker is set. If the persisted source is
    // not in the live workflows list, clear it now so the probe below can
    // register a fresh one.
    const liveWorkflowIds = new Set((workflowsData.workflows || []).map((w) => w.id));
    const persistedSource = layerSelectors.getSourceAnalysisRunId(
      useLayerStore.getState(),
      documentId,
      'issues',
    );
    if (persistedSource && !liveWorkflowIds.has(persistedSource)) {
      console.warn(
        '[useIssueLayerDetection] Clearing stale persisted source',
        { documentId, persistedSource },
      );
      clearLayerSource(documentId, 'issues');
      probedDocuments.delete(documentId);
    }

    if (probedDocuments.has(documentId)) return;
    // Another hook instance is already probing this document — skip
    if (inFlightProbes.has(documentId)) return;

    const workflows = workflowsData.workflows || [];

    const reviewWorkflows = workflows.filter(
      (w) => w.workflow_type === 'review' && (w.status === 'completed' || w.status === 'reviewing')
    );

    if (reviewWorkflows.length === 0) {
      removeAvailableLayer(documentId, 'issues');
      probedDocuments.add(documentId);
      return;
    }

    let cancelled = false;

    const probe = (async () => {
      for (const workflow of reviewWorkflows) {
        if (cancelled) return;

        try {
          const response = await issueApiService.getProjectIssuesWithModifications(workflow.id);
          if (cancelled) return;
          const issuesByDoc = response.data?.issues_by_document || {};

          let hasIssuesForDoc = false;
          for (const docIssues of Object.values(issuesByDoc)) {
            const issueArray = Array.isArray(docIssues)
              ? docIssues
              : (docIssues as any)?.issues || [];
            if (issueArray.some((issue: any) => issue.document_id === documentId)) {
              hasIssuesForDoc = true;
              break;
            }
          }

          if (hasIssuesForDoc) {
            addAvailableLayer(documentId, 'issues', workflow.id);
            probedDocuments.add(documentId);
            return;
          }
        } catch (err) {
          console.warn('[useIssueLayerDetection] Failed to probe workflow:', workflow.id, err);
        }
      }

      // All workflows checked (some may have failed) — mark probed to stop retries
      probedDocuments.add(documentId);
      removeAvailableLayer(documentId, 'issues');
    })();

    inFlightProbes.set(documentId, probe);
    probe.finally(() => inFlightProbes.delete(documentId));

    return () => {
      cancelled = true;
    };
  }, [matterId, documentId, workflowsData, addAvailableLayer, removeAvailableLayer, clearLayerSource, probeBump]);
}
